/**
 * Tailwind CSS の標準カラースケールから抜粋した生の色定数。
 * 例: Carina は `cyan` スロットに `tailwindScales.yellow` を割り当てて全シェードを yellow にする。
 *
 * 新しいスケールが必要になったら https://tailwindcss.com/docs/colors からコピペする。
 */
import type { ColorScale } from "./types";

export const tailwindScales = {
  cyan: {
    50: "#ecfeff",
    100: "#cffafe",
    200: "#a5f3fc",
    300: "#67e8f9",
    400: "#22d3ee",
    500: "#06b6d4",
    600: "#0891b2",
    700: "#0e7490",
    800: "#155e75",
    900: "#164e63",
  },
  teal: {
    50: "#f0fdfa",
    100: "#ccfbf1",
    200: "#99f6e4",
    300: "#5eead4",
    400: "#2dd4bf",
    500: "#14b8a6",
    600: "#0d9488",
    700: "#0f766e",
    800: "#115e59",
    900: "#134e4a",
  },
  yellow: {
    50: "#fefce8",
    100: "#fef9c3",
    200: "#fef08a",
    300: "#fde047",
    400: "#facc15",
    500: "#eab308",
    600: "#ca8a04",
    700: "#a16207",
    800: "#854d0e",
    900: "#713f12",
  },
  amber: {
    50: "#fffbeb",
    100: "#fef3c7",
    200: "#fde68a",
    300: "#fcd34d",
    400: "#fbbf24",
    500: "#f59e0b",
    600: "#d97706",
    700: "#b45309",
    800: "#92400e",
    900: "#78350f",
  },
  emerald: {
    50: "#ecfdf5",
    100: "#d1fae5",
    200: "#a7f3d0",
    300: "#6ee7b7",
    400: "#34d399",
    500: "#10b981",
    600: "#059669",
    700: "#047857",
    800: "#065f46",
    900: "#064e3b",
  },
  green: {
    50: "#f0fdf4",
    100: "#dcfce7",
    200: "#bbf7d0",
    300: "#86efac",
    400: "#4ade80",
    500: "#22c55e",
    600: "#16a34a",
    700: "#15803d",
    800: "#166534",
    900: "#14532d",
  },
  pink: {
    50: "#fdf2f8",
    100: "#fce7f3",
    200: "#fbcfe8",
    300: "#f9a8d4",
    400: "#f472b6",
    500: "#ec4899",
    600: "#db2777",
    700: "#be185d",
    800: "#9d174d",
    900: "#831843",
  },
  rose: {
    50: "#fff1f2",
    100: "#ffe4e6",
    200: "#fecdd3",
    300: "#fda4af",
    400: "#fb7185",
    500: "#f43f5e",
    600: "#e11d48",
    700: "#be123c",
    800: "#9f1239",
    900: "#881337",
  },
  purple: {
    50: "#faf5ff",
    100: "#f3e8ff",
    200: "#e9d5ff",
    300: "#d8b4fe",
    400: "#c084fc",
    500: "#a855f7",
    600: "#9333ea",
    700: "#7e22ce",
    800: "#6b21a8",
    900: "#581c87",
  },
  fuchsia: {
    50: "#fdf4ff",
    100: "#fae8ff",
    200: "#f5d0fe",
    300: "#f0abfc",
    400: "#e879f9",
    500: "#d946ef",
    600: "#c026d3",
    700: "#a21caf",
    800: "#86198f",
    900: "#701a75",
  },
  /** coco+ (application_id 145) 専用ブランドカラー。500 = #E8173C 固定。 */
  crimson: {
    50: "#fff1f3",
    100: "#ffe0e5",
    200: "#ffc2cc",
    300: "#ff94a5",
    400: "#ff4d63",
    500: "#e8173c",
    600: "#d10f33",
    700: "#b01030",
    800: "#8c0d27",
    900: "#5e0a1c",
  },
  /** HIBANA (application_id 132) 専用ブランドカラー。500 = #CB11DF 固定。 */
  magenta: {
    50: "#fcf3fd",
    100: "#fae7fc",
    200: "#f2c4f7",
    300: "#e894f1",
    400: "#db58e9",
    500: "#cb11df",
    600: "#ad0ebe",
    700: "#8e0c9c",
    800: "#70097b",
    900: "#510759",
  },
  /** CORUN (application_id 148) 専用ブランドカラー（桔梗色）。500 = #5B5FA8 固定。 */
  kikyo: {
    50: "#f5f5fa",
    100: "#ebebf4",
    200: "#d4d5e8",
    300: "#b6b7d8",
    400: "#9193c5",
    500: "#5b5fa8",
    600: "#4d5193",
    700: "#41447c",
    800: "#353764",
    900: "#2a2c50",
  },
} as const satisfies Record<string, ColorScale>;
